import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Button, Checkbox } from '@zep-us/zus';
import { useApplicableTrialEndDiscount } from '../hooks/useApplicableTrialEndDiscount';
import { type QuizQuoteFormValues } from '../utils/quizQuote.schema';
import { type FirstPurchasePromotion, type TrialEndQuoteDiscount } from '../utils/quoteCalculator';
import { type QuotePricingPolicy } from '../utils/quotePricingPolicy';
import { getFirstPurchaseUpsellMessage, getUpsellMessage } from '../utils/quoteUpsell';
import { QuoteUpsellBanner } from './QuoteUpsellBanner';
import { TrialEndDiscountNotice } from './TrialEndDiscountNotice';
const PRIVACY_POLICY_URL = 'https://docs.channel.io/zepquiz/ko/articles/%EA%B0%9C%EC%9D%B8%EC%A0%95%EB%B3%B4-%EC%B2%98%EB%A6%AC%EB%B0%A9%EC%B9%A8-3b3e23ce';

/** 우측 컬럼 — 견적서 발행 예시 + 동의 + 발행 버튼 (Figma right container). */
export function QuotePreviewAside({
  policy,
  firstPurchasePromotion = null,
  trialEndDiscount = null
}: {
  policy: QuotePricingPolicy | null;
  firstPurchasePromotion?: FirstPurchasePromotion | null;
  trialEndDiscount?: TrialEndQuoteDiscount | null;
}) {
  const applicableTrialEndDiscount = useApplicableTrialEndDiscount(trialEndDiscount);
  const {
    control,
    formState: {
      errors,
      isValid,
      isSubmitting
    }
  } = useFormContext<QuizQuoteFormValues>();
  const plan = useWatch<QuizQuoteFormValues, 'plan'>({
    name: 'plan'
  });
  const planType = useWatch<QuizQuoteFormValues, 'plan_type'>({
    name: 'plan_type'
  });
  const term = useWatch<QuizQuoteFormValues, 'term'>({
    name: 'term'
  });
  const userCount = useWatch<QuizQuoteFormValues, 'user_count'>({
    name: 'user_count'
  });
  const groupUpsellMessage = policy ? getUpsellMessage({
    plan,
    planType,
    term,
    userCount,
    minUserCount: policy.groupMinUserCount,
    minMonthCount: policy.groupMinMonthCount
  }) : null;
  // Basic 기간 초과로 첫 구매 할인이 빠졌으면 그 안내를 먼저 보여준다.
  // (Basic 은 학교·단체 할인 대상이 아니라 그룹 유도 문구와 겹치지 않는다.)
  const upsellMessage = getFirstPurchaseUpsellMessage({
    plan,
    term,
    promotion: firstPurchasePromotion
  }) ?? groupUpsellMessage;
  return <aside className="flex w-full flex-col gap-lg" data-sentry-component="QuotePreviewAside" data-sentry-source-file="QuotePreviewAside.tsx">
      <div className="flex flex-col gap-sm max-tablet-lg:hidden">
        <div className="flex flex-col gap-4xs">
          <h2 className="text-heading-sm-bold text-default">
            견적서 발행 예시
          </h2>
          <p className="text-body-sm-medium text-alternative">
            해당 이미지는 발행 예시이며 실제 견적서가 아닙니다.
          </p>
        </div>
        <img src="/assets/pricing/quote/quote-sample.png" alt="견적서 발행 예시" className="h-[576px] w-full rounded-sm border border-solid border-alternative object-cover shadow-[0px_4px_8px_0px_rgba(28,27,31,0.08)]" />
      </div>

      <Controller control={control} name="consent" render={({
      field
    }) => <label className="flex items-center gap-xs">
            <Checkbox checked={field.value} onCheckedChange={checked => field.onChange(checked === true)} />
            <span className="text-body-md-semibold text-default">
              견적서 발행을 위한{' '}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className="text-link" onClick={event => event.stopPropagation()}>
                개인정보 수집 및 이용
              </a>
              에 동의합니다.
            </span>
          </label>} data-sentry-element="Controller" data-sentry-source-file="QuotePreviewAside.tsx" />

      {/* 정책이 없으면 금액을 만들 수 없다 — 폼에 단가도 안 보이는 상태라 발행을 막는다. */}
      <Button type="submit" variant="contained" intent="strong" size="lg" className="h-12 w-full" disabled={!isValid || isSubmitting || !policy} data-sentry-element="Button" data-sentry-source-file="QuotePreviewAside.tsx">
        견적서 발행
      </Button>

      {!policy ? <p className="text-body-sm-medium text-alternative">
          가격 정보를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.
        </p> : null}

      {errors.root?.server?.message ? <p className="text-negative text-body-sm-medium">
          {errors.root.server.message}
        </p> : null}

      {/* 할인 적용 중에는 단체 할인 유도 배너 대신 할인 안내 카드를 보여준다
          (Figma 29114:7245 주석 — 둘이 동시에 뜨면 서로 충돌한다). */}
      {applicableTrialEndDiscount ? <TrialEndDiscountNotice discount={applicableTrialEndDiscount} /> : upsellMessage && <QuoteUpsellBanner key={upsellMessage} message={upsellMessage} />}
    </aside>;
}